import authenticationService from "@services/AuthenticationService";
import { useEffect, useState, useContext, useCallback } from "react";
import apiRequest from "@services/apiRequest";
import MaterialTable from "@material-table/core";
import {PageHeading} from "@ui-components/Container";
import { useIntl } from "react-intl";
import {UserWithRoleSchema} from "@feature/types";
import {createTheme} from "@material-ui/core/styles";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import {LinkIcon, XMarkIcon} from "@heroicons/react/24/outline";
import { TransferCapabilitiesModal } from "./TransferCapabilitiesModal";
import { DisassociateUserModal } from "./DisassociateUserModal";
import Button from "@ui-components/Button";
import {AddContactUserModal} from "./AddContactUserModal";
import {NotificationsContext} from "@ui-components/Notifications";
import {AcceptOrRefuseSection} from "./AcceptOrRefuseSection";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import {AxiosError} from "axios";
import { ProfileLogoEditor } from "../profile-logo-editor";



export const ProfileAccountUserSection = () => {
  const THEME = createTheme({
    palette: {},
    overrides: {
      MuiPaper: {
        elevation2: {
          border: "0.5px solid #4B5563",
          borderRadius: "0.8rem",
        },
      },
      MuiInputBase: {
        root: {
          border: "0",
          borderBottom: "0.5px solid #4B5563",
          borderRadius: 0,
          padding: "0 5px",
          overflow: "hidden",
          "&&&:before": { borderBottom: "0 !important", content: "none" },
          "&&&:after": { borderBottom: "0 !important", content: "none" },
          "&:focus": { border: "0", outlineOffset: "0", boxShadow: "none" },
        },
        inputTypeSearch: {
          "&:focus": { border: "0", outlineOffset: "0", boxShadow: "none" },
        },
        input: {
          "&:focus": { border: "0", outlineOffset: "0", boxShadow: "none" },
        },
      }
    },
  })
  const { push } = useContext(NotificationsContext);
  const intl = useIntl()
  const accountUserKeys = [
    {
      key: "company",
      label: intl.formatMessage({ id: "company", defaultMessage: "Azienda" }),
    },
    {
      key: "vat",
      label: intl.formatMessage({ id: "vat", defaultMessage: "Partita IVA" }),
    },
    {
      key: "stakeholder",
      label: intl.formatMessage({id: "sector", defaultMessage: "Settore"}),
    },
    {
      key: "city_and_zip_code",
      label: intl.formatMessage({id:"city", defaultMessage: "Città"}) + " - " + intl.formatMessage({id:"zip_code", defaultMessage: "CAP"}),
    }
  ];
  const countryApiRequest = useCountryAxiosInstance();
  const [contactUsers, setContactUsers] = useState<UserWithRoleSchema>([])
  const [accountUser, setAccountUser] = useState<UserWithRoleSchema | undefined>()

  const [disassociateChosenUser, setDisassociateChosenUser] = useState<UserWithRoleSchema | 'self' | undefined>()
  const [loadingDisassociate, setLoadingDisassociate] = useState(false);

  const [showAddContactUserModal, setShowAddContactUserModal] = useState(false)
  const [openTransferCapabilitiesModal, setOpenTransferCapabilitiesModal] = useState(false)
  const [pendingContactUsersInvitations, setPendingContactUsersInvitations] = useState<UserWithRoleSchema[]>([])
  const [pendingCapabilitiesTransfer, setPendingCapabilitiesTransfer] = useState<{
    account_user_id: number;
    contact_user_id: number;
    requestor_user_id: number;
  } | undefined>()
  const [accountUserThatInvitedMe, setAccountUserThatInvitedMe] = useState<UserWithRoleSchema | undefined>()
  const [hasrefreshedFirstTime, setHasRefreshedFirstTime] = useState(false)
  const userData = authenticationService.getUserData()

  const refreshToken = async () => {
    const {access_token, user_data} = await apiRequest.get('/users/refresh_token')
    authenticationService.setAuthToken(access_token, user_data)
  }
  useEffect(() => {
    refreshToken().then(() => {
      setHasRefreshedFirstTime(true)
    })
  }, [])

  const getContactUsers = async () => {
    if (userData) {
      return await apiRequest.get(`/users/contact_users`)
    }
  }

  const getPendingAssociations = async (type: string) => {
    if (userData) {
      return await apiRequest.get(
        `/users/pending_contact_users_associations`,
        {params: {type: type}}
      )
    }
  }

  const fetchContactUsers = async () => {
    const cu = await getContactUsers()
    const pinvs = await getPendingAssociations('invitation')
    const pcaptrns = await getPendingAssociations('capabilities_transfer')
    setPendingCapabilitiesTransfer(pcaptrns)
    setPendingContactUsersInvitations(pinvs)
    setContactUsers([...cu, ...pinvs])
  }

  useEffect(() => {
    if (hasrefreshedFirstTime) {
      fetchContactUsers()
    }
  }, [hasrefreshedFirstTime]);

  useEffect(() => {
    const checkIfIHaveBeenInvited = async () => {
      if (userData) {
        const r = await apiRequest.get(`/users/check_if_i_have_been_invited`)
        if (r) {
          setAccountUserThatInvitedMe(r)
        }
      }
    }
    if (hasrefreshedFirstTime)
      checkIfIHaveBeenInvited()
  }, [hasrefreshedFirstTime]);

  useEffect(() => {
    if (accountUserThatInvitedMe || pendingCapabilitiesTransfer) {
      const div = document.getElementById('accept-refuse-div')
      if (div)
        div.scrollIntoView({
          behavior: 'smooth'
        });
    }
  }, [accountUserThatInvitedMe, pendingCapabilitiesTransfer, contactUsers])

  useEffect(() => {
    const getAccountUser = async () => {
      if (userData && userData.account_user_id) {
        const r = await apiRequest.get(`/users/${userData.account_user_id}`)
        setAccountUser(r)
      }
    }
    if (hasrefreshedFirstTime)
      getAccountUser()
  }, [hasrefreshedFirstTime]);

  const acceptInvitation = async () => {
    const {access_token, user_data} = await apiRequest.post(`/users/accept_account_user_invitation`)
    authenticationService.setAuthToken(access_token, user_data)
    setAccountUserThatInvitedMe(undefined)
    window.location.reload();
  }

  const refuseInvitation = async () => {
    await apiRequest.post(`/users/refuse_account_user_invitation`)
    setAccountUserThatInvitedMe(undefined)
  }

  const getPendingCapabilitiesTransferText = () => {
    if (!pendingCapabilitiesTransfer) {
      throw new Error('Pending capabilities transfer not found')
    }
    if (pendingCapabilitiesTransfer.account_user_id ===
      pendingCapabilitiesTransfer.requestor_user_id) {
      return intl.formatMessage({
        id: "account_user_pending_capabilities_transfer_request",
        defaultMessage: `L'utente ${accountUser.firstname} ${accountUser.lastname} vuole promuoverti ad Admin dell'organizzazione ${accountUser.company}.`,
      })
    } else {
      const contactUser = contactUsers.find(cu => cu.id === pendingCapabilitiesTransfer.requestor_user_id)
      if (contactUser) {
        return intl.formatMessage({
          id: "contact_user_pending_capabilities_transfer_request",
          defaultMessage: `L'utente ${contactUser.firstname} ${contactUser.lastname} richiede di essere promosso ad Admin dell'organizzazione ${userData.company}.`,
        })
      } else {
        throw new Error('Contact user not found')
      }
    }
  }

  const getAccountUser = useCallback(() => {
    let _accountUser = accountUser;
    if(accountUser){
      _accountUser.email = _accountUser.email + " (Admin)";
      return accountUser;
    }
    return [];
  }, [accountUser]);

  return (
    <div className={"my-20"}>
      {accountUser && (
        <div>
          <div className={"flex flex-row items-center justify-between gap-2"}>
            <PageHeading
              title={intl.formatMessage({
                id: "user_type_" + accountUser.role.name,
                defaultMessage: "Società",
              })}
            />
            <Button
              styleType={"empty"}
              onClick={() => {
                setDisassociateChosenUser("self");
              }}
            >
              {intl.formatMessage({
                id: "disassociate",
                defaultMessage: "Disassocia",
              })}
              <div className={"relative"}>
                <LinkIcon className="w-6 cursor-pointer" />
                <div
                  // Diagonal bar
                  className="absolute top-0 left-1/3 w-0.5 h-full transform -rotate-45 bg-red-500 opacity-50"
                />
              </div>
            </Button>
          </div>
          <div className="border border-gray-600 bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-600">
                {accountUserKeys.map(({ key, label }) => (
                  <div
                    key={key}
                    className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6"
                  >
                    <dt className="text-sm font-medium text-gray-500">
                      {intl.formatMessage({ id: label, defaultMessage: label })}
                    </dt>
                    <dd className="mt-1 uppercase font-bold text-sm text-black-500 sm:mt-0 sm:col-span-2">
                      <div className="flex flex-row w-full">
                        <div>{
                          key === "stakeholder" ? accountUser[key] && intl.formatMessage({
                            id: accountUser[key],
                          }) :
                            key === "city_and_zip_code" ? accountUser['city'] + " - " + accountUser['zip_code'] :
                          accountUser[key] && accountUser[key]
                        }</div>
                      </div>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      )}
      {hasrefreshedFirstTime && <ProfileLogoEditor />}
      {pendingCapabilitiesTransfer &&
        pendingCapabilitiesTransfer.requestor_user_id !== userData.id &&
        ((userData.role_type === "contact_user" &&
          pendingCapabilitiesTransfer.contact_user_id === userData.id) ||
          (contactUsers.find(
            (cu) => cu.id === pendingCapabilitiesTransfer.requestor_user_id
          ) &&
            pendingCapabilitiesTransfer.account_user_id === userData.id)) && (
          <AcceptOrRefuseSection
            onAccept={async () => {
              const role: string =
                userData.role_type === "contact_user"
                  ? userData.account_user_role_type
                  : userData.role_type;
              const oldId: number = pendingCapabilitiesTransfer.account_user_id;
              const newId: number = pendingCapabilitiesTransfer.contact_user_id;

              const { access_token, user_data } = await apiRequest.post(
                `/users/accept_pending_capabilities_transfer_request`
              );
              authenticationService.setAuthToken(access_token, user_data);

              if (
                ["enterprise", "resale", "resale_tintometer"].includes(role)
              ) {
                await countryApiRequest.post(
                  "/quotations/update_after_capabilities_transfer",
                  {
                    oldId,
                    newId,
                    role,
                  }
                );
              }
              window.location.reload();
            }}
            onRefuse={async () => {
              await apiRequest.post(
                `/users/refuse_pending_capabilities_transfer_request`
              );
              setPendingCapabilitiesTransfer(undefined);
            }}
            text={getPendingCapabilitiesTransferText()}
          />
        )}
      {accountUserThatInvitedMe && (
        <AcceptOrRefuseSection
          onAccept={async () => {
            await acceptInvitation();
          }}
          onRefuse={async () => {
            await refuseInvitation();
          }}
          text={intl.formatMessage({
            id: "account_user_invitation",
            defaultMessage: `Sei stato invitato da ${accountUserThatInvitedMe.firstname} ${accountUserThatInvitedMe.lastname} a diventare utente dell'organizzazione ${accountUserThatInvitedMe.company}. Accettando, tutti i tuoi cantieri saranno traferiti all'interno dell'account ${accountUserThatInvitedMe.company}, potrai continuare a gestirli insieme ai tuoi collaboratori facenti parte della stessa organizzazione o a cui avevi condiviso il cantiere. Nel momento in cui uscissi da tale organizzazione, tutti i cantieri resteranno in capo a ${accountUserThatInvitedMe.company}. Per continuare a gestirli dovrai essere esplicitamente invitato nei cantieri di interesse.`,
          })}
        />
      )}
      {[
        "resale",
        "resale_tintometer",
        "enterprise",
        "design_studio",
        "contact_user",
      ].includes(userData.role_type) &&
        !accountUserThatInvitedMe && (
          <div>
            <PageHeading
              title={intl.formatMessage({
                id: "users",
                defaultMessage: "Utenti",
              })}
            />

            <div className="w-full flex justify-end items-baseline my-4">
              <div className="flex flex-row gap-2">
                <Button
                  styleType={"empty"}
                  onClick={() => {
                    setOpenTransferCapabilitiesModal(true);
                  }}
                  disabled={
                    (userData.role_type !== "contact_user" &&
                      contactUsers.length === 0 &&
                      !pendingCapabilitiesTransfer) ||
                    (pendingCapabilitiesTransfer &&
                      pendingCapabilitiesTransfer.requestor_user_id !==
                        userData.id)
                  }
                >
                  {pendingCapabilitiesTransfer &&
                  pendingCapabilitiesTransfer.requestor_user_id === userData.id
                    ? intl.formatMessage({
                        id: "cancel_pending_transfer",
                        defaultMessage: "Annulla richiesta promozione admin",
                      })
                    : userData.role_type === "contact_user"
                    ? intl.formatMessage({
                        id: "request_capabilities_transfer",
                        defaultMessage: "Richiedi promozione ad admin",
                      })
                    : intl.formatMessage({
                        id: "transfer_user_account",
                        defaultMessage: "promuovi admin",
                      })}
                </Button>
                {userData.role_type !== "contact_user" && (
                  <Button
                    onClick={() => {
                      setShowAddContactUserModal(true);
                    }}
                    disabled={pendingCapabilitiesTransfer}
                  >
                    {intl.formatMessage({
                      id: "add_contact_user",
                      defaultMessage: "Aggiungi utente contatto",
                    })}
                  </Button>
                )}
              </div>
            </div>

            <div>
              <MuiThemeProvider theme={THEME}>
                <MaterialTable
                  columns={[
                    ...(userData.role_type !== "contact_user"
                      ? [
                          {
                            title: intl.formatMessage({
                              id: "disassociate",
                              defaultMessage: "Disassocia",
                            }),
                            sorting: false,
                            headerStyle: { justifyItems: "center" },
                            width: 50,
                            render: (rowData) => {
                              return (
                                <div className={"grid justify-items-center"}>
                                  <div className={"relative"}>
                                    <LinkIcon
                                      className="w-6 cursor-pointer"
                                      onClick={() => {
                                        setDisassociateChosenUser(rowData);
                                      }}
                                    />
                                    <div
                                      // Diagonal bar
                                      className="absolute top-0 left-1/3 w-0.5 h-full transform -rotate-45 bg-red-500 opacity-50"
                                    />
                                  </div>
                                </div>
                              );
                            },
                          },
                        ]
                      : []),
                    {
                      title: "Email",
                      field: "email",
                      render: (rowData) => {
                        return (
                          <div>
                            {rowData.email == accountUser?.email ? (
                              <strong>(Admin) </strong>
                            ) : (
                              ""
                            )}
                            {rowData.email}
                            {!rowData.flg_active && (
                              <span className={"italic"}>
                                {" ("}
                                {intl.formatMessage({
                                  id: "pending_registration",
                                  defaultMessage: "In attesa di registrazione",
                                })}
                                {")"}
                              </span>
                            )}
                            {pendingContactUsersInvitations.find(
                              (pa) => pa.email === rowData.email
                            ) && (
                              <span className={"italic"}>
                                {" ("}
                                {intl.formatMessage({
                                  id: "pending_association",
                                  defaultMessage: "In attesa di associazione",
                                })}
                                {")"}
                              </span>
                            )}
                            {pendingCapabilitiesTransfer?.contact_user_id ===
                              rowData.id && (
                              <span className={"italic"}>
                                {" ("}
                                {intl.formatMessage({
                                  id: "pending_capabilities_transfer",
                                  defaultMessage:
                                    "In attesa di promozione admin",
                                })}
                                {")"}
                              </span>
                            )}
                          </div>
                        );
                      },
                    },
                    {
                      title: intl.formatMessage({
                        id: "firstname",
                        defaultMessage: "Nome",
                      }),
                      field: "firstname",
                    },
                    {
                      title: intl.formatMessage({
                        id: "lastname",
                        defaultMessage: "Cognome",
                      }),
                      field: "lastname",
                    },
                  ]}
                  data={[
                    ...(accountUser ? [accountUser] : []),
                    ...contactUsers,
                  ]}
                  options={{
                    showTitle: false,
                  }}
                />
              </MuiThemeProvider>
            </div>
          </div>
        )}
      {openTransferCapabilitiesModal && (
        <TransferCapabilitiesModal
          contactUsers={contactUsers}
          onConfirm={async (chosenUser?: UserWithRoleSchema) => {
            try {
              if (pendingCapabilitiesTransfer) {
                await apiRequest.post(
                  `/users/cancel_pending_capabilities_transfer_request`
                );
                push({
                  title: intl.formatMessage({
                    id: "request_canceled",
                    defaultMessage: "Richiesta annullata",
                  }),
                  type: "success",
                });
              } else {
                await apiRequest.get(`/users/request_transfer_capabilities`, {
                  params: { uid: chosenUser?.id },
                });
                push({
                  title: intl.formatMessage({
                    id: "request_sent",
                    defaultMessage: "Richiesta inviata",
                  }),
                  type: "success",
                });
              }
            } catch (e: AxiosError) {
              push({
                title: e?.response?.data?.detail,
                type: "error",
              });
            }
            await fetchContactUsers();
          }}
          onCancel={() => {
            setOpenTransferCapabilitiesModal(false);
          }}
          pendingCapabilitiesTransfer={pendingCapabilitiesTransfer}
        />
      )}
      {showAddContactUserModal && (
        <AddContactUserModal
          onExit={() => {
            setShowAddContactUserModal(false);
          }}
          onConfirm={async (email: string) => {
            try {
              const response = await apiRequest.post(
                `/users/add_contact_user`,
                { email }
              );
              push({
                title: intl.formatMessage({
                  id: "user_added",
                  defaultMessage: "Utente aggiunto",
                }),
                type: "success",
              });
              await fetchContactUsers();
            } catch (e: AxiosError) {
              push({
                title: e?.response?.data?.detail,
                type: "error",
              });
            }
          }}
        />
      )}
      {disassociateChosenUser && (
        <DisassociateUserModal
          chosenUser={disassociateChosenUser}
          onCancel={() => {
            setDisassociateChosenUser(undefined);
          }}
          onConfirm={async () => {
            try {
              if (disassociateChosenUser === "self") {
                await apiRequest.get(`/users/disassociate_contact_user`);
              } else {
                await apiRequest.get(`/users/disassociate_contact_user`, {
                  params: { uid: disassociateChosenUser.id },
                });
              }
              await countryApiRequest.post(
                "/worksites/disassociate_contact_user_from_organization",
                {
                  contact_user_id:
                    disassociateChosenUser === "self"
                      ? userData.id
                      : disassociateChosenUser.id,
                  account_user_id:
                    disassociateChosenUser === "self"
                      ? userData.account_user_id
                      : userData.id,
                }
              );
              push({
                title: intl.formatMessage({
                  id: "user_disassociated",
                  defaultMessage: "Utente disassociato",
                }),
                type: "success",
              });
              setDisassociateChosenUser(undefined);
              await refreshToken();
              window.location.reload();
            } catch (e: AxiosError) {
              push({
                title: e?.response?.data?.detail,
                type: "error",
              });
            } finally {
              setLoadingDisassociate(false);
            }
          }}
        />
      )}
    </div>
  );
}